import {
  AccountBalance as FinanceIcon,
  Badge as BadgeIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { Box, Dialog, Grid, IconButton, Tooltip } from '@mui/material';
import InfoCard from 'lib/common/GeneralComponents/TenderModal/Components/InfoCard';
import SectionCard from 'lib/common/GeneralComponents/TenderModal/Components/SectionCard';
import PDF from 'lib/common/PDF';
import AlertBanner from 'lib/designSystem/AlertBanner';
import DialogTitle from 'lib/designSystem/Title/DialogTitle';
import { BASE_COLORS } from 'lib/functions/const/colors';
import { formatDate } from 'lib/functions/utils';
import React, { useState } from 'react';

import { RowBusiness } from '.';

type Props = {
  open: boolean;
  onClose: () => void;
  business: RowBusiness | undefined;
};

const ClientDialog = ({ open, onClose, business }: Props) => {
  const [openExportDialog, setOpenExportDialog] = useState(false);

  if (!business) return null;

  const {
    businessName,
    businessIdentifier,
    companyName,
    companyId,
    expiresAt,
    active,
  } = business;

  const title = businessName ?? businessIdentifier ?? 'Cliente';

  const canDownloadReport = active;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle
          title={title}
          icon={<BusinessIcon style={{ color: BASE_COLORS.blue }} />}
          onClose={onClose}
          color={BASE_COLORS.blue}
        />
        <Box sx={{ position: 'relative' }} px={2}>
          <Box sx={{ position: 'relative' }}>
            <SectionCard title="Información del Cliente" chipLabel="Compartido">
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  zIndex: 1,
                }}
              >
                <Tooltip
                  title={
                    !canDownloadReport
                      ? 'El reporte solo está disponible cuando el cliente tiene conexión SII activa'
                      : 'Descargar reporte del cliente'
                  }
                  arrow
                >
                  <span>
                    <IconButton
                      onClick={() => {
                        setOpenExportDialog(true);
                      }}
                      disabled={!canDownloadReport}
                      size="small"
                      sx={{
                        color: BASE_COLORS.blue,
                        backgroundColor: 'rgba(15, 35, 223, 0.06)',
                        '&:hover': {
                          backgroundColor: 'rgba(15, 35, 223, 0.12)',
                        },
                        '&:disabled': {
                          color: 'text.disabled',
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      <DownloadIcon sx={{ fontSize: '1.1rem' }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <Grid container spacing={1}>
                <InfoCard
                  icon={<BadgeIcon />}
                  label="RUT"
                  valueText={businessIdentifier || 'No disponible'}
                  color="primary.main"
                />
                <InfoCard
                  icon={<FinanceIcon />}
                  label="Compartido por"
                  valueText={companyName || 'No disponible'}
                  color="primary.main"
                />
                <InfoCard
                  icon={<CalendarIcon />}
                  label="Compartido hasta"
                  valueText={
                    expiresAt
                      ? formatDate(
                          expiresAt instanceof Date
                            ? expiresAt
                            : new Date(expiresAt),
                        )
                      : 'Sin fecha de expiración'
                  }
                  color="primary.main"
                />
                <InfoCard
                  icon={
                    active ? (
                      <CheckIcon sx={{ color: 'success.main' }} />
                    ) : (
                      <CancelIcon sx={{ color: 'error.main' }} />
                    )
                  }
                  label="Estado SII"
                  valueText={active ? 'Activo' : 'Inactivo'}
                  color={active ? 'success.main' : 'error.main'}
                />
              </Grid>
              {!canDownloadReport && (
                <Box sx={{ mt: 1 }}>
                  <AlertBanner
                    severity="warning"
                    title="No hay reporte disponible"
                    message="El reporte solo está disponible cuando el cliente tiene conexión SII activa."
                  />
                </Box>
              )}
            </SectionCard>
          </Box>

          <AlertBanner
            severity="info"
            title="Cliente compartido por financiera"
            message={`Esta información fue compartida por ${companyName}. Solo tienes acceso de lectura.`}
          />
        </Box>
      </Dialog>
      <PDF
        type="datahubForFund"
        open={openExportDialog}
        onClose={() => setOpenExportDialog(false)}
        metadata={{ businessIdentifier, companyId }}
      />
    </>
  );
};

export default ClientDialog;
